import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import {
  MAX_MONTHS_WORKED_LIMIT,
  MAX_YEARS_WORKED_LIMIT,
  MIN_MONTHS_WORKED_LIMIT,
  MIN_YEARS_WORKED_LIMIT,
} from '../../constants/numberLimit';
import analytics from '../../lib/analytics';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { isPartTimeCourseSelector } from '../../reducers/selectors';

export const WorkExperiencePage = ({
  initialWorkExperience,
  dispatchAddPageData,
  dispatchWizardNav,
  isPartTimeCourse,
}) => {
  const [yearsExperience, setYearsExperience] = useState(
    initialWorkExperience?.yearsExperience || '',
  );
  const [monthsExperience, setMonthsExperience] = useState(
    initialWorkExperience?.monthsExperience || '',
  );
  const [hasChanged, setHasChanged] = useState(false);

  const isInvalid = useMemo(() => {
    if (
      (monthsExperience == null || monthsExperience.length === 0) &&
      (yearsExperience == null || yearsExperience.length === 0)
    ) {
      return true;
    }

    const months = parseInt(monthsExperience, 10);
    const years = parseInt(yearsExperience, 10);
    const totalExperience = months + years * 12;

    if (months > 12 || years > 30) {
      return true;
    }

    if (totalExperience < 0) {
      return true;
    }

    if (isPartTimeCourse && totalExperience === 0) {
      return true;
    }

    return false;
  }, [isPartTimeCourse, monthsExperience, yearsExperience]);

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'work-experience');

    const workExperience = {
      yearsExperience: yearsExperience,
      monthsExperience: monthsExperience,
    };
    const payload = payloadFactory.createAddPageDataPayload('workExperience', workExperience);
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd', resetState: hasChanged });
  };

  const handleApplyDefault = event => {
    event.preventDefault();

    analytics.sendEvent("Click - Don't have work experience", 'work-experience');

    const defaultWorkExperience = {
      monthsExperience: '0',
      yearsExperience: '0',
    };
    const payload = payloadFactory.createAddPageDataPayload(
      'workExperience',
      defaultWorkExperience,
    );
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd', resetState: true });
  };

  const handleExperienceChange = (event, setter, maxValue) => {
    const { value } = event.target;
    setHasChanged(value === '0');

    if (value === '') {
      setter('');
      return;
    }

    const regex = /^[0-9]*$/;

    if (Number.isNaN(Number(value)) || !regex.test(value)) {
      return;
    }

    const parsedValue = Number.parseInt(value, 10);
    if (parsedValue >= 0 && parsedValue <= maxValue) {
      setter(String(parsedValue));
    }
  };

  const yearsExperienceChange = event => {
    handleExperienceChange(event, setYearsExperience, MAX_YEARS_WORKED_LIMIT);
  };

  const monthsExperienceChange = event => {
    handleExperienceChange(event, setMonthsExperience, MAX_MONTHS_WORKED_LIMIT);
  };

  return (
    <PageLayout
      title="How much work experience do you have?"
      helpText="We're interested in any part-time or full-time work experience you have."
    >
      <form className="row-spacer" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--6/16-small col--4/16-medium">
            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              label="Years"
              id="years-experience"
              value={yearsExperience}
              onChange={yearsExperienceChange}
              min={MIN_YEARS_WORKED_LIMIT}
              max={MAX_YEARS_WORKED_LIMIT}
            />
          </div>

          <div className="col col--6/16-small col--4/16-medium">
            <InputField
              type="text"
              inputMode="numeric"
              pattern="\d*"
              label="Months"
              id="months-experience"
              value={monthsExperience}
              onChange={monthsExperienceChange}
              min={MIN_MONTHS_WORKED_LIMIT}
              max={MAX_MONTHS_WORKED_LIMIT}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col--auto">
            <Button type="submit" isDisabled={isInvalid}>
              Next
            </Button>
          </div>
        </div>
      </form>

      {!isPartTimeCourse && (
        <div className="row-spacer">
          <Link to="/" onClick={handleApplyDefault}>
            Don&apos;t have work experience
          </Link>
        </div>
      )}
    </PageLayout>
  );
};

WorkExperiencePage.propTypes = {
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  initialWorkExperience: PropTypes.shape({
    yearsExperience: PropTypes.string,
    monthsExperience: PropTypes.string,
  }),
  isPartTimeCourse: PropTypes.bool.isRequired,
};

WorkExperiencePage.defaultProps = {
  initialWorkExperience: {
    yearsExperience: '',
    monthsExperience: '',
  },
};

export const mapStateToProps = reduxStore => ({
  initialWorkExperience: reduxStore.builder.ui.workExperience,
  isPartTimeCourse: isPartTimeCourseSelector(reduxStore),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkExperiencePage);
